import { UseFormGetValues, UseFormSetValue } from "react-hook-form";

import { PatchQuotePayload } from "../../api/quote/updateQuote";
import {
  PersonaliseQuoteInputs,
  QuotePersonalisationScope,
} from "../../helpers/quotes";
import { PersonalisedQuoteVehicleInfo } from "../../types/Quote";
import { VehicleOptions } from "../../types/Vehicles";

export const selectedIdsFromForm = (
  record: Record<string, boolean> | undefined
): string[] => {
  if (!record) {
    return [];
  }

  return Object.keys(record).filter((id) => record[id]);
};

export const selectedIdsFromFormForOptions = (
  record: Record<string, boolean> | undefined,
  optionRecords: Record<string, { catchEOptionId: string }>
): string[] => {
  if (!record) {
    return [];
  }

  const catchEIds = new Set(
    Object.values(optionRecords).map((option) => option.catchEOptionId)
  );

  return Object.keys(record).filter((id) => record[id] && catchEIds.has(id));
};

export const hasSelectionChanged = (
  next: string[],
  previous: string[]
): boolean => {
  return (
    next.length !== previous.length ||
    !next.every((id) => previous.indexOf(id) !== -1)
  );
};

export const getManufacturerCatchEOptionIds = (
  vehicleOptions: VehicleOptions
): Set<string> => {
  return new Set(
    Object.values(vehicleOptions.options.manufacturer).map(
      (option) => option.catchEOptionId
    )
  );
};

export const buildQuoteOptionsPayload = (
  formValues: PersonaliseQuoteInputs,
  quoteVehicleInfo: PersonalisedQuoteVehicleInfo,
  vehicleOptions: VehicleOptions,
  scope: QuotePersonalisationScope
): Pick<PatchQuotePayload, "optionalEquipment" | "inclusions"> | null => {
  const { options, inclusions } = formValues;
  const oldEquipment = quoteVehicleInfo.catchEEquipmentIds;
  const oldInclusionCodes = Object.keys(
    quoteVehicleInfo.catchEInclusionSellPrices
  );

  const manufacturerCatchEIds = getManufacturerCatchEOptionIds(vehicleOptions);

  const dealerIds = selectedIdsFromFormForOptions(
    options,
    vehicleOptions.options.dealer
  );
  const manufacturerIds = selectedIdsFromFormForOptions(
    options,
    vehicleOptions.options.manufacturer
  );
  const inclusionIds = selectedIdsFromForm(inclusions);

  const preservedManufacturer = oldEquipment.filter((id) =>
    manufacturerCatchEIds.has(id)
  );
  const preservedDealer = oldEquipment.filter(
    (id) => !manufacturerCatchEIds.has(id)
  );

  let optionalEquipment: string[];
  let inclusionCodes: string[];

  switch (scope) {
    case "vehicle":
      optionalEquipment = [...dealerIds, ...preservedManufacturer];
      inclusionCodes = inclusionIds;
      break;
    case "manufacturer":
      optionalEquipment = [...manufacturerIds, ...preservedDealer];
      inclusionCodes = oldInclusionCodes;
      break;
    case "all":
      optionalEquipment = selectedIdsFromForm(options);
      inclusionCodes = inclusionIds;
      break;
  }

  const optionalEquipmentChanged = hasSelectionChanged(
    optionalEquipment,
    oldEquipment
  );
  const inclusionCodesChanged = hasSelectionChanged(
    inclusionCodes,
    oldInclusionCodes
  );

  if (!optionalEquipmentChanged && !inclusionCodesChanged) {
    return null;
  }

  return {
    optionalEquipment: optionalEquipmentChanged ? optionalEquipment : undefined,
    inclusions: inclusionCodesChanged ? inclusionCodes : undefined,
  };
};

export const prefillQuoteOptionsForm = (
  setValue: UseFormSetValue<PersonaliseQuoteInputs>,
  getValues: UseFormGetValues<PersonaliseQuoteInputs>,
  quoteVehicleInfo: PersonalisedQuoteVehicleInfo
): void => {
  const values = getValues();

  if (values.options) {
    Object.keys(values.options).forEach((optionId) => {
      setValue(`options.${optionId}`, false);
    });
  }

  quoteVehicleInfo.catchEEquipmentIds.forEach((id) => {
    setValue(`options.${id}`, true);
  });

  if (values.inclusions) {
    Object.keys(values.inclusions).forEach((inclusionId) => {
      setValue(`inclusions.${inclusionId}`, false);
    });
  }

  Object.keys(quoteVehicleInfo.catchEInclusionSellPrices).forEach((id) => {
    setValue(`inclusions.${id}`, true);
  });
};
